import { CampaignAsset } from './types';

/** Channel-specific metric fields shown on the Results tab and rendered
 *  in the lookbook PDF. Schema is kept simple — raw numbers only; rates
 *  are computed at render time. Add fields here when the scope grows
 *  (homepage impressions, collection-page views, revenue, etc.). */
export interface MetricField {
  key: string;
  label: string;
  /** Short label used in the dense PDF tile. */
  shortLabel?: string;
}

export const METRIC_FIELDS_BY_CATEGORY: Partial<Record<CampaignAsset['category'], MetricField[]>> = {
  Email: [
    { key: 'recipients', label: 'Recipients' },
    { key: 'opens', label: 'Opens' },
    { key: 'clicks', label: 'Clicks' },
  ],
  'Social Media': [
    { key: 'reach', label: 'Reach' },
    { key: 'impressions', label: 'Impressions' },
    { key: 'engagement', label: 'Engagement', shortLabel: 'Eng.' },
  ],
};

const num = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
};

const pct = (numer: number, denom: number): string => `${((numer / denom) * 100).toFixed(1)}%`;

/** Computed rates for a metrics blob, surfaced under the raw fields. */
export interface ComputedRate {
  label: string;
  value: string;
}

export const computeRates = (
  category: CampaignAsset['category'],
  metrics: Record<string, number | string | null> | undefined,
): ComputedRate[] => {
  if (!metrics) return [];
  const out: ComputedRate[] = [];
  if (category === 'Email') {
    const recipients = num(metrics.recipients);
    const opens = num(metrics.opens);
    const clicks = num(metrics.clicks);
    if (recipients && opens != null) out.push({ label: 'Open rate', value: pct(opens, recipients) });
    if (recipients && clicks != null) out.push({ label: 'CTR', value: pct(clicks, recipients) });
  } else if (category === 'Social Media') {
    const reach = num(metrics.reach);
    const engagement = num(metrics.engagement);
    if (reach && engagement != null) out.push({ label: 'Engagement rate', value: pct(engagement, reach) });
  }
  return out;
};

/** Pretty-print a metric value for the PDF / inline display. */
export const fmtMetricValue = (v: number | string | null | undefined): string => {
  const n = num(v);
  if (n == null) return '—';
  return n.toLocaleString('en');
};

/** True when any field in the metrics blob has a non-empty value. */
export const hasAnyMetric = (
  category: CampaignAsset['category'],
  metrics: Record<string, number | string | null> | undefined,
): boolean => {
  if (!metrics) return false;
  const fields = METRIC_FIELDS_BY_CATEGORY[category] ?? [];
  return fields.some((f) => num(metrics[f.key]) != null);
};
